import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "../lib/utils";
import { Button, buttonVariants, type ButtonProps, type ButtonSize } from "./button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./dropdown-menu";
import { Icon } from "./icon";

/**
 * v2 SplitButton: the wide part runs the main action, the narrow chevron opens a menu
 * of the other ones. The menu is a DropdownMenu, so `menu` takes its items (Item,
 * RadioGroup/RadioItem, Separator ...). Only `default` and `outline` are offered: the
 * two halves need a visible frame to read as one control.
 *
 * `size="chip"` is the 22px rounded chip of the ticket header's field row (status, priority,
 * assignee), so a split control sits in that row at its height and shape: micro text, 12px
 * icons, pill ends. It is the one pill-shaped control in v2, because it matches the chips
 * beside it rather than the 6px control radius.
 *
 * The label is capped (`labelMaxWidth`, a Tailwind max-width class) and truncated with an
 * ellipsis, the full text in `title` unless the caller sets one; the chevron never shrinks, so it
 * stays visible whatever the label. The group itself shrinks inside a flex row (`min-w-0
 * max-w-full`).
 */

export type SplitButtonVariant = "default" | "outline";
/** The button sizes plus `chip`, the 22px rounded chip of a field row. */
export type SplitButtonSize = ButtonSize | "chip";

export interface SplitButtonProps extends Omit<ButtonProps, "variant" | "size" | "asChild" | "trailingIcon"> {
  variant?: SplitButtonVariant;
  size?: SplitButtonSize;
  /** The chevron's accessible name, e.g. "Weitere Optionen". German UI text. */
  menuLabel: string;
  /** The menu's items. */
  menu: React.ReactNode;
  /** Menu alignment against the control; `end` keeps it under the chevron. */
  menuAlign?: "start" | "end";
  /** Tailwind max-width class capping the main part; longer labels truncate. Default `max-w-56` (14rem). */
  labelMaxWidth?: string;
}

// `chip` draws on the `sm` button and takes the chip's height, text and pill ends.
const CHIP = "h-[22px] gap-1 text-micro";

// The main part's shape and padding: square towards the chevron.
const MAIN_SHAPE: Record<SplitButtonSize, string> = {
  sm: "rounded-r-none",
  md: "rounded-r-none",
  lg: "rounded-r-none",
  chip: `${CHIP} rounded-l-full rounded-r-none px-2.5`,
};

// The chevron is narrower than a label button: same height, padding of an icon button.
const CHEVRON_SHAPE: Record<SplitButtonSize, string> = {
  sm: "rounded-l-none px-1",
  md: "rounded-l-none px-1.5",
  lg: "rounded-l-none px-2",
  chip: `${CHIP} rounded-l-none rounded-r-full pl-1.5 pr-2`,
};

// One divider between the halves: outline overlaps the two 1px borders, the filled
// variant draws a light rule on its own colour.
const DIVIDER: Record<SplitButtonVariant, string> = {
  default: "border-l border-primary-foreground/30",
  outline: "-ml-px",
};

const SplitButton = React.forwardRef<HTMLButtonElement, SplitButtonProps>(function SplitButton(
  { variant = "default", size = "md", menuLabel, menu, menuAlign = "end", labelMaxWidth = "max-w-56", className, disabled, title, children, ...props },
  ref,
) {
  const buttonSize: ButtonSize = size === "chip" ? "sm" : size;
  return (
    <div role="group" className={cn("inline-flex min-w-0 max-w-full", className)}>
      <Button
        ref={ref}
        variant={variant}
        size={buttonSize}
        disabled={disabled}
        title={title ?? (typeof children === "string" ? children : undefined)}
        // `shrink` beats the button base's `shrink-0`: the label (already `truncate`) gives way, not the chevron.
        className={cn("min-w-0 shrink", MAIN_SHAPE[size], labelMaxWidth)}
        {...props}
      >
        {children}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={menuLabel}
          title={menuLabel}
          disabled={disabled}
          className={cn(buttonVariants({ variant, size: buttonSize }), CHEVRON_SHAPE[size], DIVIDER[variant])}
        >
          <Icon icon={ChevronDown} size={size === "chip" ? 12 : 16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align={menuAlign}>{menu}</DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});

export { SplitButton };
